import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from 'vue';
import { store } from '@/store';
import VueProgrammaticInvisibleGoogleRecaptcha from 'vue-programmatic-invisible-google-recaptcha';
import TrackingService from '@/core/tracking.service';
import QuoteConfiguration = Vertica.LouisPoulsen.Application.Commerce.Requests.QuoteConfiguration;
import CookieService from '@/core/cookie.service';
import GetPrivateQuoteWebRequest = Vertica.LouisPoulsen.Application.Commerce.Requests.GetPrivateQuoteWebRequest;
import { useValidator } from '@/core/app.service';
import { useNewsletter } from '@/store/newsletter.composable';


export default /*#__PURE__*/_defineComponent({
  __name: 'GetQuoteB2C',
  props: {
    product: { default: () => ({} as ProductBaseViewModel<any>) },
    showVariantConfigurator: { type: Boolean, default: true },
    trackingName: null,
    productPackshotColor: null,
    modalMode: { type: Boolean, default: true },
    ghostButton: { type: Boolean, default: true },
    buttonClasses: { default: '' },
    selectedConfigVariantOverride: { default: null },
    hiddenProductConfigurationProperties: { default: () => [] }
  },
  emits: ["on-submit"],
  setup(__props: any, { emit }: { emit: ({(e: 'on-submit'): void}), expose: any, slots: any, attrs: any }) {

const props = __props as { product: ProductBaseViewModel<any>, showVariantConfigurator: boolean, trackingName: string, productPackshotColor: string, modalMode: boolean, ghostButton: boolean, buttonClasses: string, selectedConfigVariantOverride: ProfessionalVariantViewObject | null, hiddenProductConfigurationProperties: string[] }

const cookieKey = 'getQuote';




const { privacyPolicyStatementHtml, newsletterFlagText, profilingFlagText } = useNewsletter();

const recaptchaKey = computed<string>(() => store.getters['app/recaptchaKey']);
const primaryCountryCode = computed<string>(() => store.getters['app/primaryCountryCode']);
const footer = computed<any>(() => store.getters['app/footer']);

const selectedVariant = computed<VariantBaseViewObject>(() => store.getters['catalog/selectedVariant']);
const selectedConfigVariant = computed<ProfessionalVariantViewObject>(() => store.getters['catalog/selectedConfigVariant']);
const gaTrackingGoal = computed<string>(() => store.getters['catalog/gaTrackingGoal']);
const quoteConfiguratorOptions = computed<VariantConfiguratorOptionsViewObject[]>(() => store.getters['catalog/quoteConfiguratorOptions']);

function sendPrivateQuote(request: GetPrivateQuoteWebRequest) : Promise<any> {
    return store.dispatch('catalog/sendPrivateQuote', request);
}
function getQuoteCountries() : Promise<any> {
    return store.dispatch('catalog/getQuoteCountries');
}
function getCountryStates(countryCode: string) : Promise<StateViewObject[]> {
    return store.dispatch('catalog/getCountryStates', countryCode);
}

const form = ref<any>({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    country: '',
    state: '',
    numberOfFixtures: '',
    comments: '',
    colorNotes: '',
    marketingFlagAccepted: false,
    profilingFlagAccepted: false
});

const showModal = ref<boolean>(false);
const isSending = ref<boolean>(false);
const countries = ref<CountryViewModel[]>([]);
const states = ref<StateViewObject[]>([]);
const _uid = Math.floor(Math.random() * 1000000).toString();
const recaptchaComponentId = 'getQuoteFormRecaptcha';

const refs = { [recaptchaComponentId + _uid]: ref(null) };

function setRecaptchaRef(el: any) {
    setTimeout(() => {
        refs[recaptchaComponentId + _uid].value = el;
    }, 100);
}

const webRequestProductConfiguration = computed<QuoteConfiguration[]>(() => {
    return quoteConfiguratorOptions.value.map((productConfiguratorProp) => {
        let key = productConfiguratorProp.key;
        if (key === 'multiColor') {
            key = 'colorText';
        }
        const value = getProductConfigurationPropertyValue(key);
        if (value && value !== '-') {
            return {
                property: key,
                value
            };
        }
        return undefined;
    }).filter(_ => _);
});

const productConfiguration = computed<QuoteConfiguration[]>(() => {
    return quoteConfiguratorOptions.value.map((productConfiguratorProp) => {
        if (props.hiddenProductConfigurationProperties !== null && props.hiddenProductConfigurationProperties.includes(productConfiguratorProp.key)) {
            return undefined;
        }
        let key = productConfiguratorProp.key;
        if (key === 'multiColor') {
            key = 'colorText';
        }
        const value = getProductConfigurationPropertyValue(key);
        if (value && value !== '-') {
            return {
                property: productConfiguratorProp.label,
                value
            };
        }
        return undefined;
    }).filter(_ => _);
});

const isMarketJP = computed<boolean>(() => primaryCountryCode.value === 'JP');

const sapId = computed<string>(() => {
    const sapIdValue = (props.selectedConfigVariantOverride && props.selectedConfigVariantOverride.sapId) ||
            (selectedConfigVariant.value && selectedConfigVariant.value.sapId) ||
            (selectedVariant.value && selectedVariant.value.sapId);
    return sapIdValue || null;
});

const showColorTextArea = computed<boolean>(() => {
    return sapId.value !== null && sapId.value.endsWith('X');
});

const componentType = computed<string>(() => {
    return props.modalMode ? 'modal' : 'div';
});

onMounted(() => {
    if (!props.modalMode) {
        openModal();
    }
    form.value.numberOfFixtures = isMarketJP.value ? '1' : '';
});

watch(() => showModal.value, requestDropdownsData);
function requestDropdownsData(): void {
    if (countries.value.length === 0) {
        getQuoteCountries().then(data => {
            if (isMarketJP.value) {
                form.value.country = data.find(c => c.countryCode === 'JP');
            } else {
                countries.value = data;
            }
        });
    }
}

watch(() => form.value.country, getStates);
function getStates() {
    if (form.value.country && form.value.country.hasState) {
        getCountryStates(form.value.country.countryCode).then(data => {
            states.value = data;
        });
    } else {
        states.value = [];
    }
}

const $validator = useValidator();

function submit(): void {
    TrackingService.event('GetQuote', 'Submit');
    if (!isSending.value) {
        $validator
            .validateAll()
            .then(isValid => {
                if (isValid) {
                    const recaptchaComponent = refs[recaptchaComponentId + _uid].value as any;
                    recaptchaComponent.execute();
                } else {
                    TrackingService.event('GetQuote', 'Error');
                    scrollInvalidElementIntoView();
                }
            })
            .catch(x => {
                isSending.value = false;
                console.log('catch' + x);
            });
    }
}

function openModal(): void {
    showModal.value = true;
    TrackingService.event('Ecommerce', gaTrackingGoal.value, props.trackingName);
    if (CookieService.has(cookieKey)) {
        form.value = JSON.parse(CookieService.get(cookieKey));
    }
}

function submitForm(recaptchaToken): void {
    isSending.value = true;
    const request: GetPrivateQuoteWebRequest = createRequest(recaptchaToken);
    sendPrivateQuote(request).then(() => {
        isSending.value = false;
        if (props.modalMode) {
            showModal.value = false;
        }
        emit('on-submit');
        TrackingService.event('GetQuote', 'Success');

        CookieService.set(cookieKey, JSON.stringify(form));
    }).catch(() => {
        isSending.value = false;
    });
}

function scrollInvalidElementIntoView(): void {
    const firstInvalidElement = document.querySelector('.is-danger');
    if (firstInvalidElement) {
        firstInvalidElement.scrollIntoView({ behavior: 'smooth' });
    }
}

function createRequest(recaptchaToken: string): GetPrivateQuoteWebRequest {
    return {
        city: form.value.city,
        postalCode: form.value.postalCode,
        comments: form.value.comments,
        configurations: webRequestProductConfiguration.value.filter(item => item !== undefined),
        sapId: sapId.value,
        countryCode: form.value.country && form.value.country.countryCode,
        email: form.value.email,
        firstName: form.value.firstName,
        lastName: form.value.lastName,
        numberOfFixtures: form.value.numberOfFixtures,
        phone: form.value.phone,
        productId: props.product.productId,
        productName: props.product.productName,
        state: form.value.state && form.value.state.displayName,
        colorNotes: form.value.colorNotes,
        marketingFlagAccepted: form.value.marketingFlagAccepted,
        profilingFlagAccepted: form.value.profilingFlagAccepted,
        recaptchaToken
    };
}

function getProductConfigurationPropertyValue(propertyKey: string): any {
    if (props.selectedConfigVariantOverride === null) {
        return selectedVariant[propertyKey];
    } else {
        return props.selectedConfigVariantOverride[propertyKey] || selectedVariant[propertyKey];
    }
}


return { __sfc: true,cookieKey, props, emit, privacyPolicyStatementHtml, newsletterFlagText, profilingFlagText, recaptchaKey, primaryCountryCode, footer, selectedVariant, selectedConfigVariant, gaTrackingGoal, quoteConfiguratorOptions, sendPrivateQuote, getQuoteCountries, getCountryStates, form, showModal, isSending, countries, states, _uid, recaptchaComponentId, refs, setRecaptchaRef, webRequestProductConfiguration, productConfiguration, isMarketJP, sapId, showColorTextArea, componentType, requestDropdownsData, getStates, $validator, submit, openModal, submitForm, scrollInvalidElementIntoView, createRequest, getProductConfigurationPropertyValue, VueProgrammaticInvisibleGoogleRecaptcha }
}

})